import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Alert,
} from '@mui/material';

interface MemberSummary {
  memberId: string;
  memberName: string;
  transactionCount: number;
  totalAmount: number;
}

type DialogMode = 'enable-group-billing' | 'disable-group-billing' | 'add-members' | 'remove-members';

interface PendingTransactionsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reassign: boolean) => void;
  groupName: string;
  totalAmount: number;
  transactionCount: number;
  members: MemberSummary[];
  loading?: boolean;
  mode: DialogMode;
}

const PendingTransactionsDialog: React.FC<PendingTransactionsDialogProps> = ({
  open,
  onClose,
  onConfirm,
  groupName,
  totalAmount,
  transactionCount,
  members,
  loading = false,
  mode,
}) => {
  // Dynamic content based on mode
  const getTitle = () => {
    switch (mode) {
      case 'enable-group-billing':
        return 'Pending Charges for Group Members';
      case 'disable-group-billing':
        return 'Reassign Group Charges to Individual Members?';
      case 'add-members':
        return 'Assign Pending Charges to Group?';
      case 'remove-members':
        return 'Reassign Pending Charges to Individual Members?';
      default:
        return 'Pending Transactions';
    }
  };

  const getInfoMessage = () => {
    switch (mode) {
      case 'enable-group-billing':
        return (
          <>
            The following members of <strong>{groupName}</strong> have pending charges totaling{' '}
            <strong>${totalAmount.toFixed(2)}</strong>.
          </>
        );
      case 'disable-group-billing':
        return (
          <>
            <strong>{groupName}</strong> currently has <strong>${totalAmount.toFixed(2)}</strong> in pending charges 
            that are assigned to the primary group contact.
          </>
        );
      case 'add-members':
        return (
          <>
            The members being added to <strong>{groupName}</strong> have pending charges totaling{' '}
            <strong>${totalAmount.toFixed(2)}</strong>.
          </>
        );
      case 'remove-members':
        return (
          <>
            The members being removed from <strong>{groupName}</strong> have pending charges totaling{' '}
            <strong>${totalAmount.toFixed(2)}</strong> that are currently assigned to the primary group contact.
          </>
        );
      default:
        return null;
    }
  };

  const getQuestion = () => {
    switch (mode) {
      case 'enable-group-billing':
        return 'Should these pending charges be assigned to the primary group contact?';
      case 'disable-group-billing':
        return 'Should these pending charges be reassigned back to the individual members who incurred them?';
      case 'add-members':
        return 'Should these pending charges be assigned to the primary group contact?';
      case 'remove-members':
        return 'Should these pending charges be reassigned back to the individual members?';
      default:
        return '';
    }
  };

  const getYesExplanation = () => {
    switch (mode) {
      case 'enable-group-billing':
        return 'All pending charges will be reassigned to the primary group contact immediately.';
      case 'disable-group-billing':
        return 'All pending charges will be reassigned back to the individual members who incurred them.';
      case 'add-members':
        return 'All pending charges for these members will be reassigned to the primary group contact immediately.';
      case 'remove-members':
        return 'All pending charges will be reassigned back to the individual members.';
      default:
        return '';
    }
  };

  const getNoExplanation = () => {
    switch (mode) {
      case 'enable-group-billing':
        return 'Existing pending charges will remain assigned to individual members. Only new charges created after this change will be assigned to the primary group contact.';
      case 'disable-group-billing':
        return 'Existing pending charges will remain assigned to the primary group contact. Only new charges created after this change will be assigned to individual members.';
      case 'add-members':
        return 'Existing pending charges will remain with the individual members. Only new charges created after joining the group will be assigned to the primary group contact.';
      case 'remove-members':
        return 'Existing pending charges will remain assigned to the primary group contact. Only new charges created after removal will be assigned to individual members.';
      default:
        return '';
    }
  };

  const getYesButtonText = () => {
    switch (mode) {
      case 'enable-group-billing':
      case 'add-members':
        return 'Yes - Assign to Group';
      case 'disable-group-billing':
      case 'remove-members':
        return 'Yes - Assign to Individuals';
      default:
        return 'Yes';
    }
  };

  const getNoButtonText = () => {
    switch (mode) {
      case 'enable-group-billing':
      case 'add-members':
        return 'No - Keep Individual';
      case 'disable-group-billing':
      case 'remove-members':
        return 'No - Keep with Group';
      default:
        return 'No';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {getTitle()}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {getInfoMessage()}
          </Alert>
          <Typography variant="body1" gutterBottom>
            {getQuestion()}
          </Typography>
        </Box>

        {members.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Member Name</strong></TableCell>
                  <TableCell align="center"><strong>Transactions</strong></TableCell>
                  <TableCell align="right"><strong>Total Amount</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.memberId}>
                    <TableCell>{member.memberName}</TableCell>
                    <TableCell align="center">{member.transactionCount}</TableCell>
                    <TableCell align="right">${member.totalAmount.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell><strong>Total</strong></TableCell>
                  <TableCell align="center"><strong>{transactionCount}</strong></TableCell>
                  <TableCell align="right"><strong>${totalAmount.toFixed(2)}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Yes:</strong> {getYesExplanation()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>No:</strong> {getNoExplanation()}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => onConfirm(false)} 
          disabled={loading}
          variant="outlined"
        >
          {getNoButtonText()}
        </Button>
        <Button 
          onClick={() => onConfirm(true)} 
          disabled={loading}
          variant="contained"
          color="primary"
        >
          {getYesButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PendingTransactionsDialog;
